import { __rest } from "tslib";
import * as React from 'react';
import { ApiHooksCaching } from './caching';
import { ApiHooksEvents } from './events';
/**
 * API Hooks - Store
 * ----------------
 * Parsing of the generated API client into a set of hooks.
 *
 * This file contains everything relating to the store, includes:
 * - React context
 * - React reducer
 * - Updater component
 * - Library of actions
 */
export var ApiHooksStore;
(function (ApiHooksStore) {
    /** TYPES */
    /** UTILITIES */
    /**
     * Converts the fetching mode passed to the loading action to a loading-specific state slice status
     * @param mode the fetching mode used to invoke the fetcher.
     */
    function stateSliceStatusFromFetchingMode(mode) {
        switch (mode) {
            case 'auto':
                return 'loading-auto';
            case 'manual':
                return 'loading-manual';
            case 'refetch':
                return 'loading-refetch';
            default:
                throw new Error(`Non-active fetching mode ${mode} passed to state slice during a loading action`);
        }
    }
    ApiHooksStore.stateSliceStatusFromFetchingMode = stateSliceStatusFromFetchingMode;
    /**
     * Converts the state slice status into a fetching mode to be returned from the live response
     * @param status The current state slice status
     */
    function fetchingModeFromStateSliceStatus(status) {
        switch (status) {
            case 'loading-auto':
                return 'auto';
            case 'loading-manual':
                return 'manual';
            case 'loading-refetch':
                return 'refetch';
            case 'aborted':
                return 'aborted';
            default:
                return 'not-fetching';
        }
    }
    ApiHooksStore.fetchingModeFromStateSliceStatus = fetchingModeFromStateSliceStatus;
    /** ACTIONS */
    let Actions;
    (function (Actions) {
        /** TYPES */
        /** ACTION FACTORIES */
        /**
         * Detects a reset action vs a state update or refetch action
         * @param action The action to check
         * @returns A boolean - true if reset action, false if state update action
         */
        function isResetAction(action) {
            return !!action.reset;
        }
        Actions.isResetAction = isResetAction;
        /**
         * Detects a refetch action vs a state update or reset action
         * @param action The action to check
         * @returns A boolean - true if reset action, false if state update action
         */
        function isRefetchAction(action) {
            return !!action.refetch;
        }
        Actions.isRefetchAction = isRefetchAction;
        /**
         * Factory function for creating a 'loading' state update action
         * @param endpointKey A key specific to the endpoint (in format `controller.endpoint`)
         * @param paramHash A string representing the specific set of parameters passed to this data request
         * @param cacheKeyValue A key to cache the data by - each unique key will represent a different state slice in the dictionary.
         * @param maxCachingDepth The maximum number of data sets to store for an endpoint - comes from a query config setting
         * @returns An action object to be dispatched
         */
        function loading(endpointKey, paramHash, cacheKeyValue, mode, maxCachingDepth, isSilent = false) {
            return {
                status: stateSliceStatusFromFetchingMode(mode),
                endpointKey,
                cacheKeyValue,
                paramHash,
                maxCachingDepth,
                shouldRefetchData: undefined,
                isSilent,
            };
        }
        Actions.loading = loading;
        /**
         * Factory function for creating a 'loaded' state update action
         * @param endpointKey A key specific to the endpoint (in format `controller.endpoint`)
         * @param paramHash A string representing the specific set of parameters passed to this data request
         * @param cacheKeyValue A key to cache the data by - each unique key will represent a different state slice in the dictionary.
         * @param data The data returned from the request
         * @param maxCachingDepth The maximum number of data sets to store for an endpoint - comes from a query config setting
         * @param isSilent Setting this to "true" means that state updates will happen without a re-render
         * @returns An action object to be dispatched
         */
        function loaded(endpointKey, paramHash, cacheKeyValue, data, maxCachingDepth, timeStamp, isSilent = false) {
            return {
                status: 'loaded',
                timestamp: timeStamp !== null && timeStamp !== void 0 ? timeStamp : Date.now(),
                endpointKey,
                cacheKeyValue,
                paramHash,
                data,
                maxCachingDepth,
                error: undefined,
                shouldRefetchData: undefined,
                isSilent,
            };
        }
        Actions.loaded = loaded;
        /**
         * Factory function for creating a 'aborted' state update action
         * @param endpointKey A key specific to the endpoint (in format `controller.endpoint`)
         * @param paramHash A string representing the specific set of parameters passed to this data request
         * @param cacheKeyValue A key to cache the data by - each unique key will represent a different state slice in the dictionary.
         * @param maxCachingDepth The maximum number of data sets to store for an endpoint - comes from a query config setting
         * @param isSilent Setting this to "true" means that state updates will happen without a re-render
         * @returns An action object to be dispatched
         */
        function aborted(endpointKey, paramHash, cacheKeyValue, maxCachingDepth, isSilent = false) {
            return {
                status: 'aborted',
                endpointKey,
                cacheKeyValue,
                paramHash,
                maxCachingDepth,
                shouldRefetchData: undefined,
                isSilent,
            };
        }
        Actions.aborted = aborted;
        /**
         * Factory function for creating an 'error' state update action
         * @param endpointKey A key specific to the endpoint (in format `controller.endpoint`)
         * @param paramHash A string representing the specific set of parameters passed to this data request
         * @param cacheKeyValue A key to cache the data by - each unique key will represent a different state slice in the dictionary.
         * @param requestError The error returned from the request
         * @param maxCachingDepth The maximum number of data sets to store for an endpoint - comes from a query config setting
         * @returns An action object to be dispatched
         */
        function error(endpointKey, paramHash, cacheKeyValue, requestError, maxCachingDepth, isSilent = false) {
            return { status: 'error', endpointKey, cacheKeyValue, paramHash, error: requestError, maxCachingDepth, isSilent };
        }
        Actions.error = error;
        /**
         * Factory function for creating an 'reset' action
         * @param endpointKey (optional) Key of the endpoint to reset - resets all endpoints of not passed
         * @param cacheKeyValue (optional) A key to reset - each unique key will represent a different state slice in the dictionary.
         * @returns An action object to be dispatched
         */
        function reset(endpointKey, cacheKeyValue) {
            return { reset: true, endpointKey, cacheKeyValue };
        }
        Actions.reset = reset;
        /**
         * Factory function for creating an 'refetch' action
         * @param endpointKey (optional) Key of the endpoint to refetch - will mark all endpoints as needing a refetch of not passed
         * @param cacheKeyValue (optional) A key to refetch - each unique key will represent a different state slice in the dictionary.
         * @param refetchConfig (optional) The param override config for the refetch query.
         * @returns An action object to be dispatched
         */
        function refetch(endpointKey, cacheKeyValue, refetchConfig) {
            return { refetch: true, endpointKey, cacheKeyValue, refetchConfig };
        }
        Actions.refetch = refetch;
    })(Actions = ApiHooksStore.Actions || (ApiHooksStore.Actions = {}));
    /**  STATE FLOW - context/reducer/updater */
    /**
     * API Hooks - React context
     * - Contains the state object and dispatch method
     * - All Api hooks listen for changes from this
     */
    ApiHooksStore.Context = React.createContext([{}, undefined, undefined]);
    /**
     * Api Hooks - React reducer
     * - The single reducer for all changes in state
     * - Listens to actions dispatched from the hooks and builds the single state object
     * @param state The current state object
     * @param action The incoming action
     */
    ApiHooksStore.reducer = (state, action) => {
        var _a, _b, _c, _d, _e, _f;
        // check for reset action and reset the endpoint data
        if (Actions.isResetAction(action)) {
            const { endpointKey, cacheKeyValue } = action;
            if (endpointKey) {
                return Object.assign(Object.assign({}, state), { [endpointKey]: cacheKeyValue
                        ? Object.assign(Object.assign({}, ((_a = state[endpointKey]) !== null && _a !== void 0 ? _a : {})), { [cacheKeyValue]: undefined }) : undefined });
            }
            return {};
        }
        // check for refetch action and set the shouldRefetchData endpoint to an object on the appropriate endpoints, triggering a refetch.
        if (Actions.isRefetchAction(action)) {
            const { endpointKey, cacheKeyValue } = action;
            let stateIsModified = false;
            const newState = Object.assign({}, state);
            Object.keys(newState !== null && newState !== void 0 ? newState : {}).forEach((endpointKeyIndex) => {
                if (newState[endpointKeyIndex] && (!endpointKey || endpointKey === endpointKeyIndex)) {
                    Object.keys(newState[endpointKeyIndex]).forEach((cacheKeyValueIndex) => {
                        var _a;
                        if (newState[endpointKeyIndex][cacheKeyValueIndex] && (!cacheKeyValue || cacheKeyValue === cacheKeyValueIndex)) {
                            newState[endpointKeyIndex][cacheKeyValueIndex] = Object.assign(Object.assign({}, newState[endpointKeyIndex][cacheKeyValueIndex]), { shouldRefetchData: (_a = action.refetchConfig) !== null && _a !== void 0 ? _a : {} });
                            stateIsModified = true;
                        }
                    });
                    if (stateIsModified) {
                        newState[endpointKeyIndex] = Object.assign({}, newState[endpointKeyIndex]);
                    }
                }
            });
            if (stateIsModified) {
                return newState;
            }
            return state;
        }
        // rebuild the state object by applying changes from the action to the current state
        const { endpointKey, cacheKeyValue, isSilent } = action, stateSlice = __rest(action, ["endpointKey", "cacheKeyValue", "isSilent"]);
        if (isSilent) {
            /* eslint-disable no-param-reassign */
            state[endpointKey] = (_b = state[endpointKey]) !== null && _b !== void 0 ? _b : {};
            state[endpointKey][cacheKeyValue] = (_c = state[endpointKey][cacheKeyValue]) !== null && _c !== void 0 ? _c : { paramHash: stateSlice.paramHash };
            Object.keys(stateSlice).forEach((stateSliceKey) => {
                state[endpointKey][cacheKeyValue][stateSliceKey] = stateSlice[stateSliceKey];
            });
            state[endpointKey] = ApiHooksCaching.cleanEndpointDictionary(endpointKey, state[endpointKey], stateSlice.maxCachingDepth);
            /* eslint-enable no-param-reassign */
            return state;
        }
        const newState = Object.assign(Object.assign({}, state), { [endpointKey]: Object.assign(Object.assign({}, ((_d = state[endpointKey]) !== null && _d !== void 0 ? _d : {})), { [cacheKeyValue]: Object.assign(Object.assign({}, ((_f = (_e = state[endpointKey]) === null || _e === void 0 ? void 0 : _e[cacheKeyValue]) !== null && _f !== void 0 ? _f : {})), stateSlice) }) });
        // make sure the dictionary of state slices for this endpoint hasn't exceeded the maximum depth
        newState[endpointKey] = ApiHooksCaching.cleanEndpointDictionary(endpointKey, newState[endpointKey], stateSlice.maxCachingDepth);
        return newState;
    };
    /**
     * Api Hooks - React Provider
     * - Must be imported and wrapped around the entire app
     * - Enables re-renders downstream when state changes
     */
    ApiHooksStore.Provider = ({ children, testKeys }) => {
        /**
         * Stores the action of each dispatch so that it can be passed into event hooks
         */
        const lastActionRef = React.useRef();
        /**
         * Execute the "onBeforeInitialState" event hooks and retrieve some potential state to use
         */
        const initialState = React.useMemo(() => {
            var _a;
            return (_a = ApiHooksEvents.onBeforeInitialState.executeEventHooks(testKeys)) !== null && _a !== void 0 ? _a : {};
        }, []);
        /**
         * The root reducer
         */
        const [state, dispatch] = React.useReducer(ApiHooksStore.reducer, initialState);
        const [cacheOnlyState, dispatchCacheOnlyState] = React.useReducer(ApiHooksStore.reducer, initialState);
        /**
         * Execute the "onStateUpdated" event hooks and pass the updated state
         */
        React.useEffect(() => {
            if (ApiHooksEvents.onStateUpdated.hasEventHooks()) {
                ApiHooksEvents.onStateUpdated.executeEventHooks(state, lastActionRef.current, testKeys);
            }
        }, [state]);
        /**
         * Execute the "onCacheUpdated" event hooks and pass the updated state
         */
        React.useEffect(() => {
            if (ApiHooksEvents.onCacheUpdated.hasEventHooks()) {
                ApiHooksEvents.onCacheUpdated.executeEventHooks(cacheOnlyState, testKeys);
            }
        }, [cacheOnlyState]);
        /**
         * Override the root dispatch function.
         * - This is necessary to store the action in a ref for use elsewhere.
         */
        const dispatchOverride = React.useCallback((action) => {
            lastActionRef.current = action;
            dispatch(action);
            const { isResetAction, isRefetchAction } = ApiHooksStore.Actions;
            if (action && !isRefetchAction(action) && (isResetAction(action) || action.status === 'loaded')) {
                dispatchCacheOnlyState(action);
            }
        }, [dispatch]);
        return React.createElement(ApiHooksStore.Context.Provider, { value: [state, dispatchOverride, testKeys] }, children);
    };
    /**
     * Hook used to reset cached state.
     * @returns a function which can be used to reset cached state.
     */
    ApiHooksStore.useReset = () => {
        const [, dispatch] = React.useContext(ApiHooksStore.Context);
        /**
         * Function to reset cached state
         * - **WARNING:** If no endpoint ID is passed **all state will be reset!**
         * @param endpointId an optional endpoint ID object for resetting a specific endpoint or endpoint/cacheKey combination
         */
        const resetState = React.useCallback((endpointId) => {
            var _a;
            dispatch === null || dispatch === void 0 ? void 0 : dispatch(Actions.reset(endpointId === null || endpointId === void 0 ? void 0 : endpointId.endpointHash, (_a = endpointId === null || endpointId === void 0 ? void 0 : endpointId.cacheKeyValue) === null || _a === void 0 ? void 0 : _a.toString()));
        }, [dispatch]);
        return resetState;
    };
})(ApiHooksStore || (ApiHooksStore = {}));
